import { useEffect, useRef, useState } from "react";
import { playLock, playBloom, playScanPulse } from "./sound";

export const HOLD_MS = 3000;

// Drives the slow-mo clip: play -> hard-freeze on the Engine's exact frame -> hold -> resume -> loop -> repeat.
export const useFreezeCycle = (videoRef, errorFrame, proofId, mapPoint) => {
  const [frozen, setFrozen] = useState(false);
  const [glitch, setGlitch] = useState(false);
  const [frozenCenter, setFrozenCenter] = useState(null);
  const [fitTick, setFitTick] = useState(0);
  const frozenRef = useRef(false);
  const armedRef = useRef(true);
  const busyRef = useRef(false);
  const timers = useRef([]);
  const efRef = useRef(errorFrame);
  efRef.current = errorFrame;
  const mapRef = useRef(mapPoint);
  mapRef.current = mapPoint;

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  // The clip is source [t-1s .. t+1.2s] slowed 3x, so the flagged frame sits at min(t,1)*3s.
  const freezeAt = (v) => {
    const ef = efRef.current;
    const d = v.duration && isFinite(v.duration) ? v.duration : 6.6;
    const aligned = Math.min(typeof ef?.t === "number" ? ef.t : 1, 1) * 3;
    return Math.max(0.25, Math.min(aligned, d - 0.45));
  };

  const freeze = (v, at) => {
    frozenRef.current = true;
    armedRef.current = false;
    v.pause();
    try { v.currentTime = at; } catch (e) { /* snap to frame */ }
    const ef = efRef.current;
    setFrozenCenter(mapRef.current(ef.cx != null ? ef.cx : ef.x + ef.w / 2, ef.cy != null ? ef.cy : ef.y + ef.h / 2));
    setGlitch(true); setFrozen(true); playLock();
    timers.current.push(
      setTimeout(() => setGlitch(false), 380),
      setTimeout(playBloom, 260),
      ...[0, 1, 2].map((i) => setTimeout(playScanPulse, 400 + i * 1000)),
      setTimeout(() => {
        frozenRef.current = false;
        setFrozen(false); setFrozenCenter(null);
        v.play?.().catch(() => {});
      }, HOLD_MS),
    );
  };

  const check = (v) => {
    if (!efRef.current || frozenRef.current || busyRef.current || !v.duration) return;
    const at = freezeAt(v);
    if (v.currentTime < at - 0.15) armedRef.current = true;
    if (armedRef.current && v.currentTime >= at - 0.03) freeze(v, at);
  };

  // Park the clip on the exact technical frame, run fn(video), then lock the frame so the HUD shows.
  const withFreezeFrame = async (fn) => {
    const v = videoRef.current;
    if (!v || !efRef.current) return;
    const wasFrozen = frozenRef.current;
    busyRef.current = true;
    const at = freezeAt(v);
    try {
      if (!wasFrozen) {
        v.pause();
        await new Promise((res) => {
          const done = () => { v.removeEventListener("seeked", done); res(); };
          v.addEventListener("seeked", done);
          setTimeout(done, 1500);
          try { v.currentTime = at; } catch (e) { done(); }
        });
      }
      await fn(v, at);
    } finally {
      busyRef.current = false;
      if (!wasFrozen && !frozenRef.current) { clearTimers(); freeze(v, at); }
    }
  };

  useEffect(() => {
    clearTimers();
    frozenRef.current = false; armedRef.current = true;
    setFrozen(false); setGlitch(false); setFrozenCenter(null);
    let raf;
    const tick = () => {
      const v = videoRef.current;
      if (v && !v.paused && !v.ended) check(v);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const onR = () => setFitTick((t) => t + 1);
    window.addEventListener("resize", onR);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onR); clearTimers(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proofId]);

  const videoProps = {
    onTimeUpdate: (ev) => check(ev.target),
    onLoadedMetadata: () => setFitTick((t) => t + 1),
    // Manual loop (no `loop` attr) so the browser can never restart the clip past the freeze point.
    onEnded: (ev) => { const v = ev.target; armedRef.current = true; v.currentTime = 0; v.play?.().catch(() => {}); },
    // Nothing may resume the clip while we hold the technical moment.
    onPlay: (ev) => { if (frozenRef.current) ev.target.pause(); },
  };

  return { frozen, glitch, frozenCenter, fitTick, videoProps, withFreezeFrame };
};
